import { OrganizationSwitcher, UserButton } from "@clerk/react";
import { DarkMode, LightMode, Menu, Search } from "@mui/icons-material";
import {
  AppBar,
  Autocomplete,
  Box,
  CircularProgress,
  IconButton,
  ListItem,
  Switch,
  TextField,
  Toolbar,
  Tooltip,
  Typography,
  useColorScheme,
  useTheme,
} from "@mui/material";
import { useThrottle } from "@react-hook/throttle";
import { useQueryClient } from "@tanstack/react-query";
import { Link as TanstackLink, useNavigate, useRouter } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import {
  type SearchOut,
  SearchResultType,
  useGetCurrentOrganization,
  top,
  PricingFeature,
} from "@/models/backend";
import { useMatches } from "@tanstack/react-router";
import { useTrovePlan } from "@/hooks/trove-plan";
import { usePreviewToggle } from "@/hooks/preview";

const BypassWidget = () => {
  const { data: currentOrg } = useGetCurrentOrganization();
  return <Typography>{currentOrg?.name}</Typography>;
};

const PreviewSwitch = () => {
  const { on, setOn, shouldShow } = usePreviewToggle();
  if (!shouldShow) {
    return null;
  }
  return (
    <Tooltip title={`Preview features ${on ? "on" : "off"}`}>
      <Switch size="small" checked={on} onChange={(event) => setOn?.(event.target.checked)} />
    </Tooltip>
  );
};

const DarkModeSwitcher = () => {
  const { setMode, mode, systemMode } = useColorScheme();
  const theme = useTheme();
  return (
    <Switch
      sx={{ display: { xs: "none", sm: "flex" } }}
      checked={mode == "dark" || (mode == "system" && systemMode == "dark")}
      onChange={(event) => setMode(event.target.checked ? "dark" : "light")}
      slots={{
        thumb: (props) => (
          <span
            {...props}
            style={{
              boxShadow: (theme.vars || theme).shadows[1],
              backgroundColor: "currentColor",
              boxSizing: "border-box",
              border: "1px solid transparent",
              width: 21,
              height: 21,
              borderRadius: "50%",
            }}
          >
            {theme.palette.mode == "dark" ? (
              <DarkMode fontSize="small" sx={{ color: theme.palette.grey[100] }} />
            ) : (
              <LightMode fontSize="small" sx={{ color: theme.palette.grey[400] }} />
            )}
          </span>
        ),
      }}
    />
  );
};

export const TopBar = ({
  isSmall = false,
  onToggle = () => null,
}: {
  isSmall?: boolean;
  onToggle?: () => void;
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [searchOptions, setSearchOptions] = useState<readonly SearchOut[]>([]);
  const [searchInput, setSearchInput] = useThrottle("", 2, true);
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const router = useRouter();
  const matches = useMatches();
  const { features, main_account } = useTrovePlan();
  const { data: organization } = useGetCurrentOrganization();

  useEffect(() => {
    if (!open || searchInput === "") {
      setSearchOptions([]);
      return;
    }

    setIsLoading(true);
    top({ query: searchInput })
      .then((data) => {
        setSearchOptions(data);
        setIsLoading(false);
      })
      .catch((error) => {
        console.error("Error searching:", error);
        setIsLoading(false);
      });
  }, [searchInput, open]);

  const handleClose = () => {
    setOpen(false);
  };

  const onSelect = (value: SearchOut) => {
    if (value.type === SearchResultType.event) {
      navigate({ to: "/events/$eventId", params: { eventId: value.id } });
    } else if (value.type === SearchResultType.contact) {
      navigate({
        to: "/customers/$customerId",
        params: { customerId: value.id },
      });
    } else if (value.type === SearchResultType.customer) {
      navigate({
        to: "/customers/$customerId",
        params: { customerId: value.id },
      });
    } else if (value.type === SearchResultType.invoice) {
      navigate({
        to: "/invoices/$invoiceId",
        params: { invoiceId: value.id },
      });
    }
    setSearchInput("");
  };

  const onSelectOrganization = () => {
    queryClient.invalidateQueries();
    const last = matches[matches.length - 1];
    let newLocation;
    if (last.routeId.includes("$")) {
      newLocation = router.buildLocation({ to: ".." });
    } else {
      newLocation = router.buildLocation({ to: "." });
    }
    return newLocation.publicHref;
  };
  const adminPage = () => {
    const search = new URLSearchParams();
    search.set("link", main_account ?? organization?.id ?? "");
    return `/admin/organizations?${search.toString()}`;
  };

  return (
    <AppBar
      position="fixed"
      component="nav"
      sx={{
        zIndex: (theme) => theme.zIndex.drawer + 1,
        backgroundColor: "primary.contrastText",
        boxShadow: "none",
        borderBottomWidth: "1px",
        borderBottomColor: "divider",
        borderBottomStyle: "solid",
        "@media print": {
          display: "none",
        },
      }}
    >
      <Toolbar
        disableGutters
        sx={{
          color: "primary.main",
          paddingX: 2,
          paddingY: 1,
          gap: { xs: 1, sm: 4 },
          alignItems: "center",
          maxWidth: "100vw",
        }}
      >
        {isSmall && (
          <IconButton
            size="large"
            edge="start"
            color="inherit"
            aria-label="menu"
            onClick={onToggle}
          >
            <Menu />
          </IconButton>
        )}
        <Box
          component={TanstackLink}
          to={"/"}
          sx={{
            width: "calc(var(--SideNav-width) - 48px)",
            flexGrow: { xs: 1, sm: 0 },
            display: "flex",
            alignItems: "center",
          }}
        >
          <Box alt="Trove" component="img" height={28} src="/assets/logo.svg" />
        </Box>
        <Autocomplete
          size="small"
          sx={{ flexGrow: 1, display: { xs: "none", sm: "block" } }}
          slotProps={{
            paper: {
              elevation: 16,
              sx: {
                fontSize: "0.875rem",
              },
            },
          }}
          options={searchOptions}
          isOptionEqualToValue={(option, value) => option.title === value.title}
          open={open}
          onOpen={() => setOpen(true)}
          onClose={handleClose}
          onChange={(_, value) => value && onSelect(value)}
          onInputChange={(_, value) => setSearchInput(value)}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder="Search"
              sx={{
                "& .MuiInputBase-root": { fontSize: "0.875rem" },
                "& .MuiInputBase-input": { py: "4px" },
              }}
              slotProps={{
                ...params.slotProps,
                input: {
                  ...params.slotProps?.input,
                  endAdornment: (
                    <>{isLoading ? <CircularProgress color="inherit" size={16} /> : null}</>
                  ),
                  startAdornment: <Search sx={{ fontSize: "1.25rem" }} />,
                },
              }}
            />
          )}
          getOptionLabel={(option) => option.title}
          renderOption={(params, option) => (
            <ListItem {...params} sx={{ fontSize: "0.875rem" }}>
              {option.title}
              <Typography variant="body2" color="textDisabled" sx={{ textTransform: "capitalize" }}>
                &nbsp;- {option.type}
              </Typography>
            </ListItem>
          )}
        />
        <DarkModeSwitcher />
        {import.meta.env.VITE_IS_PREVIEW && <PreviewSwitch />}
        {import.meta.env.VITE_IS_BYPASS ? (
          <BypassWidget />
        ) : (
          <OrganizationSwitcher
            appearance={{
              variables: { fontSize: "var(--mui-font-body1)" },
              elements: {
                organizationSwitcherTrigger: {
                  font: "var(--mui-font-h6)",
                },
              },
            }}
            hidePersonal={true}
            afterSelectOrganizationUrl={onSelectOrganization}
            afterSelectPersonalUrl={onSelectOrganization}
            afterCreateOrganizationUrl={
              features.includes(PricingFeature.multi_account) ? adminPage() : undefined
            }
          />
        )}
        <UserButton
          appearance={{
            variables: { fontSize: "var(--mui-font-body1)" },
          }}
        />
      </Toolbar>
    </AppBar>
  );
};
